import { useEffect, useMemo, useState } from 'react';
import { Card, CardHeader, CardTitle } from '@components/common/Card';
import { Button } from '@components/common/Button';
import { Input } from '@components/common/Input';
import { Dialog } from '@components/common/Dialog';
import { LoadingSpinner } from '@components/common/LoadingSpinner';
import {
  useCreateRole,
  useGetRole,
  useListPermissions,
  useListRoles,
  useSetRolePermissions,
} from '@api/hooks/useRoles';
import type { Permission, Role } from '@schemas/api';

const groupPermissionsByResource = (permissions: Permission[]) =>
  Object.entries(
    permissions.reduce<Record<string, Permission[]>>((groups, permission) => {
      if (!groups[permission.resource]) {
        groups[permission.resource] = [];
      }
      groups[permission.resource].push(permission);
      return groups;
    }, {})
  )
    .sort(([left], [right]) => left.localeCompare(right))
    .map(([resource, items]) => ({
      resource,
      items: [...items].sort((left, right) => left.action.localeCompare(right.action)),
    }));

const togglePermission = (permissionIds: string[], permissionId: string) =>
  permissionIds.includes(permissionId)
    ? permissionIds.filter((id) => id !== permissionId)
    : [...permissionIds, permissionId];

export const AdminRoleManagement = () => {
  const [skip, setSkip] = useState(0);
  const [selectedRoleId, setSelectedRoleId] = useState<string | null>(null);
  const [isCreateDialogOpen, setIsCreateDialogOpen] = useState(false);
  const [newRoleName, setNewRoleName] = useState('');
  const [newRoleDescription, setNewRoleDescription] = useState('');
  const [newRolePermissionIds, setNewRolePermissionIds] = useState<string[]>([]);
  const [editablePermissionIds, setEditablePermissionIds] = useState<string[]>([]);

  const limit = 10;
  const { data: rolesData, isLoading: rolesLoading } = useListRoles(skip, limit);
  const { data: selectedRole, isLoading: selectedRoleLoading } = useGetRole(selectedRoleId || '');
  const { data: permissions = [], isLoading: permissionsLoading } = useListPermissions();
  const createRoleMutation = useCreateRole();
  const setRolePermissionsMutation = useSetRolePermissions();

  const roles = rolesData?.items || [];
  const total = rolesData?.total || 0;
  const totalPages = Math.ceil(total / limit);
  const currentPage = Math.floor(skip / limit) + 1;
  const permissionGroups = useMemo(() => groupPermissionsByResource(permissions), [permissions]);
  const hasUnsavedPermissionChanges = useMemo(() => {
    if (!selectedRole) return false;
    const currentIds = new Set((selectedRole.permissions ?? []).map((permission) => permission.id));
    const draftIds = new Set(editablePermissionIds);
    if (currentIds.size !== draftIds.size) return true;
    return [...currentIds].some((id) => !draftIds.has(id));
  }, [editablePermissionIds, selectedRole]);

  useEffect(() => {
    if (selectedRole) {
      setEditablePermissionIds((selectedRole.permissions ?? []).map((permission) => permission.id));
    } else {
      setEditablePermissionIds([]);
    }
  }, [selectedRole]);

  if (rolesLoading || permissionsLoading) return <LoadingSpinner />;

  const resetCreateDialog = () => {
    setNewRoleName('');
    setNewRoleDescription('');
    setNewRolePermissionIds([]);
    setIsCreateDialogOpen(false);
  };

  const handleCreateRole = async () => {
    const createdRole = await createRoleMutation.mutateAsync({
      name: newRoleName.trim(),
      description: newRoleDescription.trim() || undefined,
      permission_ids: newRolePermissionIds,
    });
    resetCreateDialog();
    setSelectedRoleId(createdRole.id);
  };

  const handleSavePermissions = async () => {
    if (!selectedRole) return;
    await setRolePermissionsMutation.mutateAsync({
      roleId: selectedRole.id,
      permissionIds: editablePermissionIds,
    });
  };

  return (
    <div className="space-y-6">
      <Card>
        <CardHeader>
          <div className="flex items-center justify-between gap-4">
            <CardTitle>Role Management</CardTitle>
            <Button onClick={() => setIsCreateDialogOpen(true)}>
              Create Role
            </Button>
          </div>
        </CardHeader>

        <div className="p-6">
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-gray-200 dark:border-gray-800">
                  <th className="text-left py-3 px-4 font-semibold">Role Name</th>
                  <th className="text-left py-3 px-4 font-semibold">Description</th>
                  <th className="text-left py-3 px-4 font-semibold">Permissions</th>
                  <th className="text-left py-3 px-4 font-semibold">Type</th>
                  <th className="text-right py-3 px-4 font-semibold">Actions</th>
                </tr>
              </thead>
              <tbody>
                {roles.map((role: Role) => {
                  const permissionCount = role.permission_count ?? role.permissions?.length ?? 0;
                  return (
                    <tr
                      key={role.id}
                      className="border-b border-gray-200 dark:border-gray-800 hover:bg-gray-50 dark:hover:bg-gray-900/50"
                    >
                      <td className="py-3 px-4 font-medium">{role.name}</td>
                      <td className="py-3 px-4 text-gray-600 dark:text-gray-400">
                        {role.description || '-'}
                      </td>
                      <td className="py-3 px-4">{permissionCount} permissions</td>
                      <td className="py-3 px-4">
                        <span
                          className={`inline-block px-2 py-1 rounded-full text-xs font-semibold ${
                            role.is_system_role
                              ? 'bg-purple-100 text-purple-800 dark:bg-purple-900/30 dark:text-purple-300'
                              : 'bg-blue-100 text-blue-800 dark:bg-blue-900/30 dark:text-blue-300'
                          }`}
                        >
                          {role.is_system_role ? 'System' : 'Custom'}
                        </span>
                      </td>
                      <td className="py-3 px-4 text-right">
                        <Button
                          variant="secondary"
                          size="sm"
                          onClick={() => setSelectedRoleId(role.id)}
                        >
                          {role.is_system_role ? 'View Details' : 'Manage'}
                        </Button>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>

          <div className="flex justify-between items-center pt-4">
            <p className="text-sm text-gray-600 dark:text-gray-400">
              Showing {skip + 1}-{Math.min(skip + limit, total)} of {total} roles
            </p>
            <div className="flex gap-2">
              <Button
                variant="secondary"
                disabled={skip === 0}
                onClick={() => setSkip(Math.max(0, skip - limit))}
              >
                Previous
              </Button>
              <span className="text-sm py-2 px-4">
                Page {currentPage} of {totalPages || 1}
              </span>
              <Button
                variant="secondary"
                disabled={currentPage >= totalPages}
                onClick={() => setSkip(skip + limit)}
              >
                Next
              </Button>
            </div>
          </div>
        </div>
      </Card>

      {selectedRole && !selectedRoleLoading && (
        <Card>
          <CardHeader>
            <div className="flex justify-between items-center gap-4">
              <div>
                <CardTitle>Role Details: {selectedRole.name}</CardTitle>
                <p className="mt-1 text-sm text-gray-600 dark:text-gray-400">
                  {selectedRole.is_system_role
                    ? 'System roles are read-only templates available to every tenant.'
                    : 'Custom roles are tenant-scoped and can be updated here.'}
                </p>
              </div>
              <div className="flex gap-2">
                {!selectedRole.is_system_role && (
                  <Button
                    onClick={handleSavePermissions}
                    disabled={!hasUnsavedPermissionChanges}
                    loading={setRolePermissionsMutation.isPending}
                  >
                    Save Permissions
                  </Button>
                )}
                <Button
                  variant="secondary"
                  onClick={() => setSelectedRoleId(null)}
                >
                  Close
                </Button>
              </div>
            </div>
          </CardHeader>

          <div className="p-6 space-y-6">
            {selectedRole.description && (
              <div>
                <h4 className="font-semibold text-gray-900 dark:text-gray-50 mb-2">
                  Description
                </h4>
                <p className="text-gray-600 dark:text-gray-400">
                  {selectedRole.description}
                </p>
              </div>
            )}

            <div>
              <div className="flex items-center justify-between gap-4 mb-3">
                <h4 className="font-semibold text-gray-900 dark:text-gray-50">
                  Permissions ({editablePermissionIds.length})
                </h4>
                {!selectedRole.is_system_role && hasUnsavedPermissionChanges && (
                  <span className="text-xs font-medium text-amber-600 dark:text-amber-400">
                    Unsaved changes
                  </span>
                )}
              </div>

              <div className="space-y-4">
                {permissionGroups.map((group) => (
                  <div
                    key={group.resource}
                    className="rounded-lg border border-gray-200 dark:border-gray-700"
                  >
                    <div className="border-b border-gray-200 dark:border-gray-700 px-4 py-3">
                      <h5 className="font-semibold text-gray-900 dark:text-gray-50">
                        {group.resource}
                      </h5>
                    </div>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-3 p-4">
                      {group.items.map((permission) => {
                        const checked = editablePermissionIds.includes(permission.id);
                        return (
                          <label
                            key={permission.id}
                            className={`flex items-start gap-3 rounded-lg border px-3 py-3 ${
                              checked
                                ? 'border-blue-300 bg-blue-50 dark:border-blue-700 dark:bg-blue-950/30'
                                : 'border-gray-200 dark:border-gray-700'
                            } ${
                              selectedRole.is_system_role
                                ? 'cursor-default opacity-90'
                                : 'cursor-pointer'
                            }`}
                          >
                            <input
                              type="checkbox"
                              className="mt-1"
                              checked={checked}
                              disabled={selectedRole.is_system_role}
                              onChange={() =>
                                setEditablePermissionIds((current) =>
                                  togglePermission(current, permission.id)
                                )
                              }
                            />
                            <div>
                              <p className="font-mono text-sm font-medium text-gray-900 dark:text-gray-50">
                                {permission.resource}.{permission.action}
                              </p>
                              {permission.description && (
                                <p className="mt-1 text-xs text-gray-600 dark:text-gray-400">
                                  {permission.description}
                                </p>
                              )}
                            </div>
                          </label>
                        );
                      })}
                    </div>
                  </div>
                ))}
              </div>
            </div>

            <div>
              <h4 className="font-semibold text-gray-900 dark:text-gray-50 mb-2">
                Role Type
              </h4>
              <span
                className={`inline-block px-3 py-1 rounded-full text-sm font-semibold ${
                  selectedRole.is_system_role
                    ? 'bg-purple-100 text-purple-800 dark:bg-purple-900/30 dark:text-purple-300'
                    : 'bg-blue-100 text-blue-800 dark:bg-blue-900/30 dark:text-blue-300'
                }`}
              >
                {selectedRole.is_system_role ? 'System Role' : 'Custom Role'}
              </span>
            </div>
          </div>
        </Card>
      )}

      <Dialog
        open={isCreateDialogOpen}
        onOpenChange={setIsCreateDialogOpen}
        className="max-w-6xl"
      >
        <div className="flex max-h-[85vh] w-full flex-col">
          <div className="flex items-start justify-between gap-4 border-b border-gray-200 dark:border-gray-800 px-6 pb-4 pt-6">
            <div>
              <h2 className="text-xl font-semibold text-gray-900 dark:text-gray-50">
                Create Custom Role
              </h2>
              <p className="mt-1 text-sm text-gray-600 dark:text-gray-400">
                Custom roles are scoped to the active tenant and can be assigned to users in your organization.
              </p>
            </div>
            <Button variant="secondary" onClick={resetCreateDialog}>
              Cancel
            </Button>
          </div>

          <div className="flex-1 overflow-y-auto px-6 pb-6 pt-6">
            <div className="space-y-6">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <Input
                label="Role Name"
                value={newRoleName}
                onChange={(event) => setNewRoleName(event.target.value)}
                placeholder="e.g. AP Reviewer"
              />
              <div>
                <label className="block text-sm font-medium text-content-secondary mb-1.5">
                  Description
                </label>
                <textarea
                  value={newRoleDescription}
                  onChange={(event) => setNewRoleDescription(event.target.value)}
                  rows={3}
                  className="input min-h-[96px]"
                  placeholder="Describe when this role should be assigned."
                />
              </div>
            </div>

            <div className="space-y-4">
              <div className="flex items-center justify-between gap-4">
                <h3 className="font-semibold text-gray-900 dark:text-gray-50">
                  Permissions ({newRolePermissionIds.length})
                </h3>
                <Button
                  variant="secondary"
                  size="sm"
                  onClick={() => setNewRolePermissionIds(permissions.map((permission) => permission.id))}
                >
                  Select All
                </Button>
              </div>

              <div className="max-h-[420px] overflow-y-auto space-y-4 pr-1">
                {permissionGroups.map((group) => (
                  <div
                    key={group.resource}
                    className="rounded-lg border border-gray-200 dark:border-gray-700"
                  >
                    <div className="flex items-center justify-between gap-4 border-b border-gray-200 dark:border-gray-700 px-4 py-3">
                      <h4 className="font-semibold text-gray-900 dark:text-gray-50">
                        {group.resource}
                      </h4>
                      <Button
                        variant="secondary"
                        size="sm"
                        onClick={() =>
                          setNewRolePermissionIds((current) => {
                            const next = new Set(current);
                            const allSelected = group.items.every((permission) => next.has(permission.id));
                            group.items.forEach((permission) => {
                              if (allSelected) {
                                next.delete(permission.id);
                              } else {
                                next.add(permission.id);
                              }
                            });
                            return [...next];
                          })
                        }
                      >
                        Toggle Group
                      </Button>
                    </div>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-3 p-4">
                      {group.items.map((permission) => {
                        const checked = newRolePermissionIds.includes(permission.id);
                        return (
                          <label
                            key={permission.id}
                            className={`flex items-start gap-3 rounded-lg border px-3 py-3 cursor-pointer ${
                              checked
                                ? 'border-blue-300 bg-blue-50 dark:border-blue-700 dark:bg-blue-950/30'
                                : 'border-gray-200 dark:border-gray-700'
                            }`}
                          >
                            <input
                              type="checkbox"
                              className="mt-1"
                              checked={checked}
                              onChange={() =>
                                setNewRolePermissionIds((current) =>
                                  togglePermission(current, permission.id)
                                )
                              }
                            />
                            <div>
                              <p className="font-mono text-sm font-medium text-gray-900 dark:text-gray-50">
                                {permission.resource}.{permission.action}
                              </p>
                              {permission.description && (
                                <p className="mt-1 text-xs text-gray-600 dark:text-gray-400">
                                  {permission.description}
                                </p>
                              )}
                            </div>
                          </label>
                        );
                      })}
                    </div>
                  </div>
                ))}
              </div>
            </div>

            <div className="sticky bottom-0 flex justify-end gap-3 border-t border-gray-200 bg-white pt-4 dark:border-gray-800">
              <Button variant="secondary" onClick={resetCreateDialog}>
                Cancel
              </Button>
              <Button
                onClick={handleCreateRole}
                disabled={!newRoleName.trim()}
                loading={createRoleMutation.isPending}
              >
                Create Role
              </Button>
            </div>
            </div>
          </div>
        </div>
      </Dialog>
    </div>
  );
};
